/*
  LIMELIGHT product page upsells.
  Bundle tiers, delivery cut-off countdown, free delivery progress, the mobile
  sticky buy bar, and one-tap cross sell. Written to the same plain-ES5 shape as
  limelight-parity.js so it runs without a build step.
*/
(function () {
  'use strict';

  var SELECTORS = {
    bundle: '[data-limelight-bundle]',
    tier: '[data-limelight-tier]',
    cutoff: '[data-limelight-cutoff]',
    cutoffValue: '[data-limelight-cutoff-value]',
    stickyBar: '[data-limelight-sticky-atc]',
    stickyAdd: '[data-limelight-sticky-add]',
    stickyPrice: '[data-limelight-sticky-price]',
    stickyMeta: '[data-limelight-sticky-meta]',
    socialProof: '[data-limelight-social-proof]',
    bottomAppBars: '#PBarNextFrameWrapper, #floatingBuyNowButton, .rsi_floating_bottom',
    crossSell: '[data-limelight-cross-sell]',
    crossSellList: '[data-limelight-cross-sell-list]',
    crossSellAdd: '[data-limelight-cross-sell-add]',
    crossSellLabel: '[data-limelight-cross-sell-label]'
  };

  function formatMoney(cents) {
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(cents, theme.settings && theme.settings.moneyFormat);
    }

    return String(Math.round(cents / 100));
  }

  function cartAddUrl() {
    return (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js';
  }

  /* ------------------------------------------------------------ Bundle tiers */

  function initBundle(bundle) {
    if (bundle.dataset.limelightInitialized === 'true') {
      return;
    }

    var tiers = Array.prototype.slice.call(bundle.querySelectorAll(SELECTORS.tier));
    var formId = bundle.dataset.formId;
    var form = formId ? document.getElementById(formId) : null;

    if (!tiers.length) {
      return;
    }

    function quantityInput() {
      // The quantity field lives outside the form element and is bound by the
      // `form` attribute, so query the document rather than the form itself.
      return document.querySelector('input[name="quantity"][form="' + formId + '"]')
        || (form ? form.querySelector('input[name="quantity"]') : null);
    }

    function select(tier) {
      var quantity = Number(tier.dataset.quantity || 1);
      var total = Number(tier.dataset.total || 0);
      var input = quantityInput();
      var radio = tier.querySelector('input[type="radio"]');

      tiers.forEach(function (candidate) {
        candidate.classList.toggle('is-selected', candidate === tier);
      });

      if (radio) {
        radio.checked = true;
      }

      if (input) {
        input.value = quantity;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      document.dispatchEvent(new CustomEvent('limelight:bundle:change', {
        detail: { quantity: quantity, total: total }
      }));
    }

    tiers.forEach(function (tier) {
      var radio = tier.querySelector('input[type="radio"]');

      tier.addEventListener('click', function () {
        select(tier);
      });

      // Keyboard users move between tiers with the arrow keys, which fires
      // `change` on the radio without ever clicking the label.
      if (radio) {
        radio.addEventListener('change', function () {
          if (radio.checked) {
            select(tier);
          }
        });
      }
    });

    var preselected = bundle.querySelector('.limelight-tier.is-selected') || tiers[0];
    select(preselected);

    bundle.dataset.limelightInitialized = 'true';
  }

  /* ------------------------------------------------------ Delivery cut-off */

  function initCutoff(element) {
    if (element.dataset.limelightInitialized === 'true') {
      return;
    }

    var endsAt = Number(element.dataset.endsAt || 0) * 1000;
    var output = element.querySelector(SELECTORS.cutoffValue);
    var timer;

    if (!endsAt || !output) {
      element.hidden = true;
      return;
    }

    function update() {
      var remaining = endsAt - Date.now();

      if (remaining <= 0) {
        // The cut-off rolled over; the dated estimate is stale until reload.
        element.hidden = true;
        window.clearInterval(timer);
        return;
      }

      var totalMinutes = Math.floor(remaining / 60000);
      var hours = Math.floor(totalMinutes / 60);
      var minutes = totalMinutes % 60;

      output.textContent = hours > 0
        ? hours + 'h ' + minutes + 'm'
        : minutes + 'm';
    }

    update();
    timer = window.setInterval(update, 30000);
    element.dataset.limelightInitialized = 'true';
  }

  /* ------------------------------------------------------- Sticky buy bar */

  function initStickyAtc(bar) {
    if (bar.dataset.limelightInitialized === 'true') {
      return;
    }

    var formId = bar.dataset.formId;
    var trigger = bar.querySelector(SELECTORS.stickyAdd);
    var priceOutput = bar.querySelector(SELECTORS.stickyPrice);
    var metaOutput = bar.querySelector(SELECTORS.stickyMeta);
    var lastState = null;

    // The theme re-renders the product form after load, so holding a reference
    // to the form or its button leaves us pointing at a detached node. Look both
    // up on demand instead.
    function currentForm() {
      return (formId && document.getElementById(formId)) || null;
    }

    function currentAnchor() {
      var form = currentForm();
      return (form && form.querySelector('[data-add-to-cart]'))
        || document.querySelector('[data-add-to-cart]');
    }

    if (!trigger || !currentAnchor()) {
      return;
    }

    bar.hidden = false;

    // Deliberately synchronous. Throttling through requestAnimationFrame needs a
    // latch, and a backgrounded tab starves the callback that clears it, which
    // leaves the bar dead for the rest of the session once the shopper returns.
    // One rect read per scroll event is cheap, and the class is only written
    // when the answer actually changes.
    function evaluate() {
      var anchor = currentAnchor();
      if (!anchor) {
        return;
      }

      var scrolledPast = anchor.getBoundingClientRect().bottom < 0;

      if (scrolledPast === lastState) {
        return;
      }

      lastState = scrolledPast;
      bar.classList.toggle('is-visible', scrolledPast);

      // Lets the social proof toast lift clear of the buy bar instead of
      // sitting underneath it.
      document.documentElement.classList.toggle('limelight-buybar-visible', scrolledPast);
    }

    window.addEventListener('scroll', evaluate, { passive: true });
    window.addEventListener('resize', evaluate);

    // An observer alone is not enough: a jump scroll takes the button from
    // below the viewport to above it without ever intersecting, so no callback
    // fires. It is kept as a cheap second trigger alongside the scroll handler.
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(evaluate, { threshold: 0 });
      var observed = currentAnchor();

      if (observed) {
        observer.observe(observed);
      }

      document.addEventListener('shopify:section:load', function () {
        var next = currentAnchor();
        observer.disconnect();
        if (next) {
          observer.observe(next);
        }
        evaluate();
      });
    }

    evaluate();

    trigger.addEventListener('click', function () {
      var form = currentForm();
      if (!form) {
        return;
      }

      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    });

    document.addEventListener('limelight:bundle:change', function (event) {
      if (!priceOutput || !event.detail) {
        return;
      }

      priceOutput.textContent = formatMoney(event.detail.total);

      if (metaOutput) {
        metaOutput.textContent = event.detail.quantity > 1
          ? event.detail.quantity + ' suits'
          : '';
      }
    });

    bar.dataset.limelightInitialized = 'true';
  }

  /* ------------------------------------------------ Third party bottom bars */

  var appBarWatcherStarted = false;
  var appBarTimer;

  function measureBottomAppBars() {
    // Apps such as the Releasit COD form pin a bar to the bottom of the screen
    // as a direct child of body. The cart drawer lives inside the header
    // section, so its own stacking context cannot beat that bar no matter what
    // z-index it uses, and the bar ends up covering the checkout button.
    // Publishing the bar's height lets the drawer and our own bottom-anchored
    // UI move clear of it instead of fighting over z-index.
    var viewportBottom = window.innerHeight;
    var tallest = 0;

    document.querySelectorAll(SELECTORS.bottomAppBars).forEach(function (element) {
      var style = window.getComputedStyle(element);

      if (style.position !== 'fixed' || style.display === 'none' || style.visibility === 'hidden') {
        return;
      }

      var rect = element.getBoundingClientRect();

      // Only count bars that actually hug the bottom edge.
      if (rect.height < 8 || Math.abs(rect.bottom - viewportBottom) > 6) {
        return;
      }

      if (rect.height > tallest) {
        tallest = rect.height;
      }
    });

    document.documentElement.style.setProperty('--limelight-app-bar-height', Math.round(tallest) + 'px');
  }

  function scheduleAppBarMeasure() {
    window.clearTimeout(appBarTimer);
    appBarTimer = window.setTimeout(measureBottomAppBars, 120);
  }

  function initBottomAppBars() {
    if (appBarWatcherStarted) {
      return;
    }

    appBarWatcherStarted = true;
    measureBottomAppBars();

    // App scripts inject their bars well after DOMContentLoaded, so keep
    // re-measuring for a while and whenever body level nodes change.
    [800, 2000, 4000, 8000].forEach(function (delay) {
      window.setTimeout(measureBottomAppBars, delay);
    });

    window.addEventListener('resize', scheduleAppBarMeasure);
    window.addEventListener('orientationchange', scheduleAppBarMeasure);
    document.addEventListener('ajaxProduct:added', scheduleAppBarMeasure);
    document.addEventListener('cart:build', scheduleAppBarMeasure);

    if (window.MutationObserver) {
      new MutationObserver(scheduleAppBarMeasure).observe(document.body, { childList: true });
    }
  }

  /* ---------------------------------------------------------- Social proof */

  function initSocialProof(root) {
    if (root.dataset.limelightInitialized === 'true') {
      return;
    }

    root.dataset.limelightInitialized = 'true';

    var toast = root.querySelector('[data-limelight-proof-toast]');
    var payload = root.querySelector('[data-limelight-proof-data]');
    var dismissKey = 'limelight-proof-dismissed';
    var config;

    if (!toast || !payload) {
      return;
    }

    try {
      config = JSON.parse(payload.textContent);
    } catch (error) {
      return;
    }

    function lines(value) {
      return String(value || '')
        .split('\n')
        .map(function (entry) { return entry.trim(); })
        .filter(Boolean);
    }

    var products = config.products || [];
    var names = lines(config.names);
    var cities = lines(config.cities);

    if (!products.length || !names.length || !cities.length) {
      return;
    }

    try {
      if (window.sessionStorage.getItem(dismissKey) === '1') {
        return;
      }
    } catch (error) {
      // Storage is unavailable; the toast simply cannot be muted for the visit.
    }

    var firstDelay = Number(root.dataset.firstDelay || 8) * 1000;
    var interval = Number(root.dataset.interval || 22) * 1000;
    var duration = Number(root.dataset.duration || 6) * 1000;
    var maxToasts = Number(root.dataset.max || 8);
    var minMinutes = Number(root.dataset.minMinutes || 3);
    var maxMinutes = Math.max(minMinutes + 1, Number(root.dataset.maxMinutes || 90));

    var whoOutput = toast.querySelector('[data-limelight-proof-who]');
    var titleOutput = toast.querySelector('[data-limelight-proof-title]');
    var timeOutput = toast.querySelector('[data-limelight-proof-time]');
    var imageOutput = toast.querySelector('[data-limelight-proof-image]');
    var linkOutput = toast.querySelector('[data-limelight-proof-link]');
    var closeButton = toast.querySelector('[data-limelight-proof-close]');

    var shown = 0;
    var hideTimer;
    var nextTimer;
    var stopped = false;
    var lastProductIndex = -1;

    function pick(list) {
      return list[Math.floor(Math.random() * list.length)];
    }

    function pickProduct() {
      // Avoid showing the same product twice in a row when there is a choice.
      var index = Math.floor(Math.random() * products.length);

      if (products.length > 1 && index === lastProductIndex) {
        index = (index + 1) % products.length;
      }

      lastProductIndex = index;
      return products[index];
    }

    function describeAge() {
      var minutes = minMinutes + Math.floor(Math.random() * (maxMinutes - minMinutes + 1));
      var prefix = config.timePrefix ? config.timePrefix + ' ' : '';

      if (minutes < 60) {
        return prefix + minutes + ' minutes ago';
      }

      var hours = Math.round(minutes / 60);
      return prefix + hours + (hours === 1 ? ' hour ago' : ' hours ago');
    }

    function hide() {
      toast.classList.remove('is-visible');
      window.setTimeout(function () {
        if (!toast.classList.contains('is-visible')) {
          toast.hidden = true;
        }
      }, 350);
    }

    function show() {
      if (stopped || shown >= maxToasts) {
        return;
      }

      var product = pickProduct();
      var city = pick(cities);

      if (config.region) {
        city += ', ' + config.region;
      }

      // Reads as: Ayesha in Lahore, PK purchased a
      whoOutput.textContent = [pick(names), config.connector, city, config.verb]
        .filter(Boolean)
        .join(' ');
      titleOutput.textContent = product.title;
      timeOutput.textContent = describeAge();
      linkOutput.href = product.url;

      if (product.image) {
        imageOutput.src = product.image;
        imageOutput.hidden = false;
      } else {
        imageOutput.hidden = true;
      }

      toast.hidden = false;
      // Force a frame so the entry transition runs from the hidden state.
      window.requestAnimationFrame(function () {
        toast.classList.add('is-visible');
      });

      shown += 1;
      hideTimer = window.setTimeout(hide, duration);
      nextTimer = window.setTimeout(show, duration + interval);
    }

    function stop() {
      stopped = true;
      window.clearTimeout(hideTimer);
      window.clearTimeout(nextTimer);
      hide();

      try {
        window.sessionStorage.setItem(dismissKey, '1');
      } catch (error) {
        // Nothing to persist to; the toast stays muted for this page only.
      }
    }

    if (closeButton) {
      closeButton.addEventListener('click', stop);
    }

    window.setTimeout(show, firstDelay);
  }

  /* ------------------------------------------------------------ Cross sell */

  function bindCrossSellButtons(section) {
    section.querySelectorAll(SELECTORS.crossSellAdd).forEach(function (button) {
      if (button.dataset.limelightBound === 'true') {
        return;
      }

      button.addEventListener('click', function () {
        var label = button.querySelector(SELECTORS.crossSellLabel);
        var original = label ? label.textContent : '';

        button.classList.add('is-loading');

        window.fetch(cartAddUrl(), {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: [{ id: Number(button.dataset.variantId), quantity: 1 }]
          })
        })
          .then(function (response) {
            return response.json();
          })
          .then(function (data) {
            button.classList.remove('is-loading');

            if (data && data.status && data.status !== 200) {
              return;
            }

            button.classList.add('is-added');
            if (label) {
              label.textContent = 'Added';
            }

            // Reuses the theme's own handler, which rebuilds and opens the drawer.
            document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
              detail: { product: data }
            }));

            window.setTimeout(function () {
              button.classList.remove('is-added');
              if (label) {
                label.textContent = original;
              }
            }, 2500);
          })
          .catch(function () {
            button.classList.remove('is-loading');
          });
      });

      button.dataset.limelightBound = 'true';
    });
  }

  function initCrossSell(section) {
    if (section.dataset.limelightInitialized === 'true') {
      return;
    }

    var url = section.dataset.recommendationsUrl;
    var list = section.querySelector(SELECTORS.crossSellList);

    section.dataset.limelightInitialized = 'true';
    bindCrossSellButtons(section);

    if (!url || !list || !window.fetch) {
      return;
    }

    window.fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        return response.ok ? response.text() : null;
      })
      .then(function (markup) {
        if (!markup) {
          return;
        }

        var parsed = new DOMParser().parseFromString(markup, 'text/html');
        var incoming = parsed.querySelector(SELECTORS.crossSellList);

        if (!incoming || !incoming.children.length) {
          section.hidden = true;
          return;
        }

        list.innerHTML = incoming.innerHTML;
        bindCrossSellButtons(section);
      })
      .catch(function () {
        section.hidden = true;
      });
  }

  /* ------------------------------------------------------------------ Boot */

  function init(root) {
    var scope = root || document;

    // The sticky bar listens for the bundle's first selection event, so it has
    // to be wired up before the bundle announces its default tier.
    scope.querySelectorAll(SELECTORS.stickyBar).forEach(initStickyAtc);
    scope.querySelectorAll(SELECTORS.bundle).forEach(initBundle);
    scope.querySelectorAll(SELECTORS.cutoff).forEach(initCutoff);
    scope.querySelectorAll(SELECTORS.crossSell).forEach(initCrossSell);
    scope.querySelectorAll(SELECTORS.socialProof).forEach(initSocialProof);
    initBottomAppBars();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
}());
